import { useState, useEffect } from "react";
import { Button, Spinner, ButtonColor, Checkbox } from "@swivl-mono/ui";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { API } from "@swivl-mono/api-helper";
import { Member } from "../../../Models/Member/Member.Current";
import { extractSheetId, parseEmails } from "./shared/onboarding.utils";
import { styles } from "./shared/onboarding.styles";
import AppsModel from "../../../Models/Apps/Apps.Model";
import { worksheetMessages } from "./shared/worksheet_messages";

interface GoogleSheetData {
    headers: string[];
    records: Record<string, string>[];
}

interface WorksheetInfo {
    name: string;
    faqContent: string;
    isCustomized: boolean;
    isDuplicate?: boolean;
}

interface UnableToCustomizeWorksheet {
    name: string;
    rowNumber: number;
    reasons: {
        notSupported: boolean;
        noFaqContent: boolean;
    };
}

interface ProcessingResult {
    success: boolean;
    processed: number;
    failed: number;
    errors: string[];
    successfulWorksheets: string[];
    failedWorksheets: string[];
}

export function WorksheetCustomizationView() {
    const [sheetUrl, setSheetUrl] = useState("");
    const [emails, setEmails] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoadingSheet, setIsLoadingSheet] = useState(false);
    const [isCheckingCustomization, setIsCheckingCustomization] = useState(false);
    const [hasMandrill, setHasMandrill] = useState(false);
    const [isCheckingMandrill, setIsCheckingMandrill] = useState(true);

    const [sheetData, setSheetData] = useState<GoogleSheetData | null>(null);
    const [customizableWorksheets, setCustomizableWorksheets] = useState<WorksheetInfo[]>([]);
    const [unableToCustomizeWorksheets, setUnableToCustomizeWorksheets] = useState<UnableToCustomizeWorksheet[]>([]);
    const [duplicateWorksheets, setDuplicateWorksheets] = useState<string[]>([]);
    const [selectedWorksheets, setSelectedWorksheets] = useState<Set<string>>(new Set());
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showAcceptableLabels, setShowAcceptableLabels] = useState(false);
    const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });

    useEffect(() => {
        checkMandrill();
    }, []);


    const checkMandrill = async () => {
        try {
            await AppsModel.load(false);
            const mandrillApp = AppsModel.state.apps?.find(app => app.type === 'mandrill');
            setHasMandrill(!!mandrillApp?.token?.mandrill_api_key);
        } catch (error) {
            console.warn("Failed to check Mandrill configuration:", error);
            setHasMandrill(false);
        } finally {
            setIsCheckingMandrill(false);
        }
    };


    const loadSheetData = async () => {
        if (!sheetUrl.trim()) {
            AlertsModel.errorToast("Please enter a Google Sheet URL");
            return;
        }

        setIsLoadingSheet(true);
        setCustomizableWorksheets([]);
        setUnableToCustomizeWorksheets([]);
        setDuplicateWorksheets([]);
        setSelectedWorksheets(new Set());

        try {
            const sheetId = extractSheetId(sheetUrl);

            // Try to read the "Cortex" sheet
            const response = await API.Google.readGoogleSheet(sheetId, "Cortex!A:B");

            if (!response || !response.records || response.records.length === 0) {
                throw new Error('No data found in "Cortex" sheet');
            }

            // Check for required columns
            if (!response.headers.includes("Worksheet") || !response.headers.includes("FAQ Content")) {
                throw new Error('Sheet must have "Worksheet" in Column A and "FAQ Content" in Column B');
            }

            setSheetData(response);

            // Process the worksheet data
            await processWorksheetData(response);

        } catch (error) {
            console.error("Error loading sheet:", error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            if (errorMessage.includes('not found') || errorMessage.includes('404')) {
                AlertsModel.errorToast('Sheet "Cortex" not found. Please ensure your Google Sheet has a sheet named "Cortex"');
            } else {
                AlertsModel.errorToast(`Failed to load sheet: ${errorMessage}`);
            }
        } finally {
            setIsLoadingSheet(false);
        }
    };

    const processWorksheetData = async (data: GoogleSheetData) => {
        setIsCheckingCustomization(true);

        const worksheetLabels = worksheetMessages.worksheets.map(w => w.label);
        const seenWorksheets = new Set<string>();
        const duplicates: string[] = [];
        const customizable: WorksheetInfo[] = [];
        const unableToCustomize: UnableToCustomizeWorksheet[] = [];

        // Process each row
        data.records.forEach((record, index) => {
            const worksheetName = record["Worksheet"]?.trim();
            const faqContent = record["FAQ Content"]?.trim() || "";
            const rowNumber = index + 2; // +2 because index is 0-based and row 1 is headers

            if (!worksheetName) return;

            // Check for duplicates
            if (seenWorksheets.has(worksheetName)) {
                if (!duplicates.includes(worksheetName)) {
                    duplicates.push(worksheetName);
                }
                return; // Skip duplicate entries
            }
            seenWorksheets.add(worksheetName);

            const isSupported = worksheetLabels.includes(worksheetName);
            const hasFaqContent = !!faqContent;

            // Categorize worksheets
            if (isSupported && hasFaqContent) {
                // Can be customized
                customizable.push({
                    name: worksheetName,
                    faqContent,
                    isCustomized: false // Will be checked next
                });
            } else {
                // Cannot be customized
                unableToCustomize.push({
                    name: worksheetName,
                    rowNumber,
                    reasons: {
                        notSupported: !isSupported,
                        noFaqContent: !hasFaqContent
                    }
                });
            }
        });

        // Check customization status for customizable worksheets
        const orgId = Member.orgId;
        if (orgId && customizable.length > 0) {
            try {
                // Get all variables for each worksheet to check customization status
                for (const worksheet of customizable) {
                    const worksheetConfig = worksheetMessages.worksheets.find(w => w.label === worksheet.name);
                    if (worksheetConfig) {
                        const isCustomized = await checkWorksheetCustomization(orgId, worksheetConfig);
                        worksheet.isCustomized = isCustomized;
                    }
                }
            } catch (error) {
                console.warn("Error checking customization status:", error);
            }
        }

        setCustomizableWorksheets(customizable);
        setUnableToCustomizeWorksheets(unableToCustomize);
        setDuplicateWorksheets(duplicates);
        setIsCheckingCustomization(false);
    };

    const checkWorksheetCustomization = async (orgId: number, worksheetConfig: any): Promise<boolean> => {
        // Collect all variables to check (message variables and enable switches)
        const variablesToCheck: string[] = [];

        for (const message of worksheetConfig.messages) {
            variablesToCheck.push(message.variable);
            if (message.enableSwitchVar) {
                variablesToCheck.push(message.enableSwitchVar);
            }
        }

        // Check if any of these variables exist specifically at org level only
        try {
            // Load all variables for the org and filter for org-level only
            const allVariables = await API.LocationVars.loadVariables(orgId);

            // Filter to only variables that are at org level (groupId and locationId are null)
            const orgLevelVariables = allVariables.filter(variable =>
                variable.groupId === null &&
                variable.locationId === null
            );

            // Check if any of our worksheet variables exist at org level
            const existingOrgVariables = orgLevelVariables.filter(variable =>
                variablesToCheck.includes(variable.key)
            );

            return existingOrgVariables.length > 0;
        } catch (error) {
            console.warn(`Error checking customization for ${worksheetConfig.label}:`, error);
            return false;
        }
    };

    const handleSelectAll = () => {
        const allNames = customizableWorksheets.map(w => w.name);
        setSelectedWorksheets(new Set(allNames));
    };

    const handleDeselectAll = () => {
        setSelectedWorksheets(new Set());
    };

    const toggleWorksheetSelection = (worksheetName: string) => {
        const newSelection = new Set(selectedWorksheets);
        if (newSelection.has(worksheetName)) {
            newSelection.delete(worksheetName);
        } else {
            newSelection.add(worksheetName);
        }
        setSelectedWorksheets(newSelection);
    };

    const handleCustomize = () => {
        if (selectedWorksheets.size === 0) {
            AlertsModel.errorToast("Please select at least one worksheet to customize");
            return;
        }

        // Since we only show customizable worksheets, all selected worksheets should be valid

        setShowConfirmDialog(true);
    };

    const executeCustomization = async () => {
        setShowConfirmDialog(false);
        setIsProcessing(true);
        setProcessingProgress({ current: 0, total: selectedWorksheets.size });

        const result: ProcessingResult = {
            success: false,
            processed: 0,
            failed: 0,
            errors: [],
            successfulWorksheets: [],
            failedWorksheets: []
        };

        try {
            const orgId = Member.orgId;
            if (!orgId) {
                throw new Error("No org selected");
            }

            const emailArray = parseEmails(emails);
            const selectedWorksheetsArray = Array.from(selectedWorksheets);

            // Process in batches of 5
            const batchSize = 5;
            let processedCount = 0;

            for (let i = 0; i < selectedWorksheetsArray.length; i += batchSize) {
                const batch = selectedWorksheetsArray.slice(i, i + batchSize);

                for (const worksheetName of batch) {
                    processedCount++;
                    setProcessingProgress({ current: processedCount, total: selectedWorksheets.size });

                    try {
                        // Get worksheet data and config
                        const worksheet = customizableWorksheets.find(w => w.name === worksheetName);
                        const worksheetConfig = worksheetMessages.worksheets.find(w => w.label === worksheetName);

                        if (!worksheet || !worksheetConfig) {
                            throw new Error(`Worksheet configuration not found for ${worksheetName}`);
                        }

                        // Call the AI service endpoint
                        const customizationResponse = await API.AI.customizeWorksheetMessages(
                            orgId,
                            worksheetName,
                            worksheet.faqContent,
                            worksheetConfig
                        );

                        if (!customizationResponse.success) {
                            throw new Error(customizationResponse.error || 'Customization failed');
                        }

                        result.processed++;
                        result.successfulWorksheets.push(worksheetName);
                    } catch (error) {
                        result.failed++;
                        result.failedWorksheets.push(worksheetName);
                        result.errors.push(`${worksheetName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                }

                // Add delay between batches
                if (i + batchSize < selectedWorksheetsArray.length) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            result.success = result.processed > 0;

            // Send email notification if configured
            if (emailArray.length > 0 && hasMandrill) {
                await sendEmailNotification(result, emailArray);
            }

            // Show results
            if (result.success) {
                if (result.failed === 0) {
                    AlertsModel.successToast(`Successfully customized ${result.processed} worksheet(s)`);
                } else {
                    AlertsModel.errorToast(
                        `Customized ${result.processed} worksheet(s), ${result.failed} failed. Check details below.`
                    );
                }
            } else {
                AlertsModel.errorToast("Customization failed for all selected worksheets");
            }

            // Reset UI to initial state after customization (success or failure)
            setSheetData(null);
            setCustomizableWorksheets([]);
            setUnableToCustomizeWorksheets([]);
            setDuplicateWorksheets([]);
            setSelectedWorksheets(new Set());
            setShowConfirmDialog(false);
            setShowAcceptableLabels(false);

        } catch (error) {
            console.error("Customization error:", error);
            AlertsModel.errorToast(`Customization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);

            // Reset UI to initial state on error
            setSheetData(null);
            setCustomizableWorksheets([]);
            setUnableToCustomizeWorksheets([]);
            setDuplicateWorksheets([]);
            setSelectedWorksheets(new Set());
            setShowConfirmDialog(false);
            setShowAcceptableLabels(false);
        } finally {
            setIsProcessing(false);
            setProcessingProgress({ current: 0, total: 0 });
        }
    };

    const sendEmailNotification = async (result: ProcessingResult, emailArray: string[]) => {
        try {
            await AppsModel.load(false);
            const mandrillApp = AppsModel.state.apps?.find(app => app.type === 'mandrill');

            if (!mandrillApp?.token?.mandrill_api_key) {
                throw new Error("Mandrill not configured");
            }

            const fromEmail = mandrillApp.data?.mandrill_from_email || 'info@tryswivl.com';
            const fromName = mandrillApp.data?.mandrill_from_name || 'swivl';
            const orgName = Member.org?.name || 'Your Organization';

            const subject = `Worksheet Customization Complete - ${orgName}`;
            const body = `Hi there!

The worksheet customization for ${orgName} has completed.

Summary:
• Processed: ${result.processed} worksheets
• Failed: ${result.failed} worksheets

${result.successfulWorksheets.length > 0 ? `✅ Successfully customized worksheets:\n${result.successfulWorksheets.map(w => `  • ${w}`).join('\n')}\n` : ''}
${result.failedWorksheets.length > 0 ? `❌ Failed worksheets:\n${result.failedWorksheets.map(w => `  • ${w}`).join('\n')}\n` : ''}
${result.errors.length > 0 ? `\n🔍 Error details:\n${result.errors.slice(0, 5).map(e => `  • ${e}`).join('\n')}${result.errors.length > 5 ? `\n  • ... and ${result.errors.length - 5} more` : ''}\n` : ''}

Questions? Feel free to reach out to the Product team.`;

            const apiKey = mandrillApp.token.mandrill_api_key;

            for (const email of emailArray) {
                const message = {
                    from_email: fromEmail,
                    from_name: fromName,
                    to: [{ email: email.trim(), type: "to" }],
                    subject: subject,
                    text: body
                };

                try {
                    const response = await fetch('https://mandrillapp.com/api/1.0/messages/send.json', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            key: apiKey,
                            message: message
                        })
                    });

                    const responseData = await response.json();

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }

                    // Check Mandrill-specific response status
                    const emailResult = responseData[0]; // Mandrill returns array with one result per email
                    if (emailResult && (emailResult.status === 'rejected' || emailResult.status === 'invalid')) {
                        throw new Error(`Mandrill rejected email: ${emailResult.reject_reason || 'Unknown reason'}`);
                    }

                } catch (emailError) {
                    console.error(`❌ [WORKSHEET_EMAIL] Failed to send email to ${email.trim()}:`, emailError instanceof Error ? emailError.message : emailError);
                    // Continue with other emails even if one fails
                }
            }

        } catch (error) {
            console.warn("Failed to send email notification:", error);
        }
    };

    const hasCustomizedWorksheets = customizableWorksheets.some(w => w.isCustomized);

    return (
        <div style={styles.container}>
            <h2 style={styles.heading}>
                4️⃣ Customize Worksheet Messages
            </h2>
            <p style={styles.description}>
                Import worksheet content from a Google Sheet to customize Cortex responses at the ORG level.
            </p>

            <div style={styles.requirementsBox}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>Requirements:</h4>
                <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                    <li>
                        {isCheckingMandrill ? (
                            <>⏳ Checking email configuration...</>
                        ) : hasMandrill ? (
                            <>✅ Mandrill configured for email notifications</>
                        ) : (
                            <>❌ <a href="/settings/apps/mandrill" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc' }}>Mandrill</a> needs to be configured</>
                        )}
                    </li>
                    <li>📝 Google Sheet must have a sheet named "Cortex"</li>
                    <li>📊 Column A: "Worksheet" (worksheet names)</li>
                    <li>📊 Column B: "FAQ Content" (content to use)</li>
                </ol>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <input
                    type="url"
                    value={sheetUrl}
                    onChange={(e) => setSheetUrl(e.target.value)}
                    placeholder="Google Sheet URL: https://docs.google.com/spreadsheets/d/..."
                    disabled={isProcessing || isLoadingSheet}
                    style={styles.input}
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    placeholder="Email addresses for notifications (comma-separated)"
                    disabled={isProcessing}
                    style={styles.input}
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <Button
                    onClick={loadSheetData}
                    disabled={isProcessing || isLoadingSheet || !sheetUrl.trim()}
                >
                    {isLoadingSheet ? (
                        <>
                            <Spinner />
                            {' Loading Sheet...'}
                        </>
                    ) : (
                        sheetData ? "Refresh Sheet Data" : "Load Sheet Data"
                    )}
                </Button>
            </div>

            {/* Duplicate Worksheets Warning */}
            {duplicateWorksheets.length > 0 && (
                <div style={{
                    ...styles.errorBox,
                    backgroundColor: '#fef3c7',
                    borderColor: '#f59e0b'
                }}>
                    <h3 style={{ ...styles.errorHeading, color: '#d97706' }}>
                        Duplicate Worksheets Found
                    </h3>
                    <p style={{ fontSize: '14px', color: '#374151', marginBottom: '8px' }}>
                        The following worksheets appear multiple times in the sheet. Only the first occurrence will be used:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                        {duplicateWorksheets.map(name => (
                            <li key={name}>{name}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Customizable Worksheets */}
            {customizableWorksheets.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px' }}>
                        Select Worksheets to Customize
                    </h3>

                    {customizableWorksheets.some(w => w.isCustomized) && customizableWorksheets.some(w => w.isCustomized && selectedWorksheets.has(w.name)) && (
                        <div style={{
                            padding: '10px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '6px',
                            marginBottom: '10px',
                            fontSize: '14px',
                            color: '#92400e'
                        }}>
                            ⚠️ Running this for worksheets that are already customized may overwrite some or all the current customizations with new customizations.
                        </div>
                    )}

                    <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                        <Button
                            onClick={handleSelectAll}
                            disabled={isProcessing}
                            color={ButtonColor.LIGHT_GREY}
                        >
                            Select All
                        </Button>
                        <Button
                            onClick={handleDeselectAll}
                            disabled={isProcessing}
                            color={ButtonColor.LIGHT_GREY}
                        >
                            Deselect All
                        </Button>
                    </div>

                    <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '10px',
                        position: 'relative'
                    }}>
                        {customizableWorksheets.map((worksheet, index) => (
                            <div
                                key={worksheet.name}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px',
                                    fontSize: '14px',
                                    backgroundColor: selectedWorksheets.has(worksheet.name) ? '#f0f9ff' : 'transparent',
                                    borderRadius: '4px',
                                    marginBottom: '4px'
                                }}
                            >
                                <Checkbox
                                    checked={selectedWorksheets.has(worksheet.name)}
                                    onCheckedChange={() => toggleWorksheetSelection(worksheet.name)}
                                    disabled={isProcessing}
                                    name={worksheet.name}
                                />
                                <label
                                    style={{
                                        marginLeft: '8px',
                                        cursor: 'pointer',
                                        flex: 1
                                    }}
                                    onClick={() => !isProcessing && toggleWorksheetSelection(worksheet.name)}
                                >
                                    {worksheet.name}
                                    {worksheet.isCustomized && (
                                        <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                                            (already customized)
                                        </span>
                                    )}
                                </label>
                                <div style={{ position: 'relative', marginLeft: '8px' }}>
                                    <button
                                    style={{
                                        padding: '6px',
                                        fontSize: '14px',
                                        backgroundColor: '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        width: '28px',
                                        height: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                        const button = e.currentTarget;
                                        const tooltip = button.nextElementSibling as HTMLElement;
                                        if (tooltip) {
                                            const rect = button.getBoundingClientRect();
                                            tooltip.style.display = 'block';
                                            tooltip.style.position = 'fixed';
                                            tooltip.style.left = `${rect.left - 400}px`; // Position to the left of button

                                            // Check if we're in the top half of the viewport
                                            if (rect.top < window.innerHeight / 2) {
                                                tooltip.style.top = `${rect.bottom + 8}px`;
                                                tooltip.style.bottom = 'auto';
                                            } else {
                                                tooltip.style.bottom = `${window.innerHeight - rect.top + 8}px`;
                                                tooltip.style.top = 'auto';
                                            }
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (tooltip) tooltip.style.display = 'none';
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    ⓘ
                                </button>
                                    <div
                                        style={{
                                            display: 'none',
                                            position: 'fixed',
                                            padding: '12px',
                                            backgroundColor: 'white',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                            width: '400px',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            zIndex: 10000,
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                                            FAQ Content for {worksheet.name}:
                                        </div>
                                        <div style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                            {worksheet.faqContent}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Unable to Customize Worksheets */}
            {unableToCustomizeWorksheets.length > 0 && (
                <div style={styles.errorBox}>
                    <h3 style={styles.errorHeading}>
                        Unable to Customize
                    </h3>

                    {unableToCustomizeWorksheets.filter(w => w.reasons.notSupported).length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151', marginBottom: '4px' }}>
                                Not Supported:
                            </div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                                These worksheet names are not supported by this customization feature. Please double-check the names match supported worksheets (case-sensitive).
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                                {unableToCustomizeWorksheets
                                    .filter(w => w.reasons.notSupported)
                                    .map(worksheet => (
                                        <li key={`${worksheet.name}-${worksheet.rowNumber}`}>
                                            {worksheet.name} (Row {worksheet.rowNumber})
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    )}

                    <button
                        onClick={() => setShowAcceptableLabels(!showAcceptableLabels)}
                        style={{
                            marginTop: '10px',
                            marginBottom: '12px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            background: 'none',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {showAcceptableLabels ? 'Hide' : 'Show'} supported worksheet labels
                    </button>

                    {showAcceptableLabels && (
                        <select
                            style={{
                                ...styles.input,
                                marginBottom: '12px',
                                fontSize: '12px',
                                height: '150px'
                            }}
                            multiple
                            disabled
                        >
                            {worksheetMessages.worksheets
                                .map(w => w.label)
                                .sort((a, b) => a.localeCompare(b))
                                .map(label => (
                                    <option key={label}>{label}</option>
                                ))
                            }
                        </select>
                    )}

                    {unableToCustomizeWorksheets.filter(w => !w.reasons.notSupported && w.reasons.noFaqContent).length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151', marginBottom: '4px' }}>
                                No FAQ Content:
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                                {unableToCustomizeWorksheets
                                    .filter(w => !w.reasons.notSupported && w.reasons.noFaqContent)
                                    .map(worksheet => (
                                        <li key={`${worksheet.name}-${worksheet.rowNumber}`}>
                                            {worksheet.name} (Row {worksheet.rowNumber})
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Customize Button */}
            {customizableWorksheets.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                    <Button
                        onClick={handleCustomize}
                        disabled={isProcessing || selectedWorksheets.size === 0 || (!!emails.trim() && !hasMandrill)}
                    >
                        {isProcessing ? (
                            <>
                                <Spinner />
                                {processingProgress.total > 0
                                    ? ` Customizing worksheet ${processingProgress.current}/${processingProgress.total}...`
                                    : ' Processing...'}
                            </>
                        ) : (
                            "Customize Worksheet Messages"
                        )}
                    </Button>
                </div>
            )}

            {/* Processing Message */}
            {isProcessing && (
                <p style={{ marginTop: '15px', fontSize: '13px', color: '#666' }}>
                    ⚠️ This process may take several minutes. You'll receive an email when complete. Feel free to navigate away from this page.
                </p>
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        maxWidth: '500px',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                        <h2 style={{ marginTop: 0, fontWeight: 700, fontSize: '20px', color: '#1f2937' }}>Confirm Customization for {Member.org?.name || 'Unknown'}</h2>
                        <p>You are about to customize the following {selectedWorksheets.size} worksheet(s):</p>
                        <div style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            padding: '10px',
                            marginBottom: '15px'
                        }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {Array.from(selectedWorksheets).map(name => {
                                    const worksheet = customizableWorksheets.find(w => w.name === name);
                                    return (
                                        <li key={name}>
                                            {name}
                                            {worksheet?.isCustomized && (
                                                <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                                                    (already customized)
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        {customizableWorksheets.some(w => w.isCustomized && selectedWorksheets.has(w.name)) && (
                            <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '10px' }}>
                                Worksheets already customized will be overwritten. This cannot be undone.
                            </p>
                        )}
                        <p style={{ color: '#374151', fontSize: '14px', marginBottom: '20px' }}>
                            Worksheets will be customized at the org level. Group and location customization not supported.
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setShowConfirmDialog(false)}>
                                Cancel
                            </Button>
                            <Button onClick={executeCustomization}>
                                Confirm & Customize for {Member.org?.name || 'Unknown'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}